import { useState } from 'react';
import { useListFetch, useModal, useRequest } from '../../../../hooks';
import { notify } from '../../../../app';
import {
  getPresentations,
  createPresentation,
  updatePresentation,
  deletePresentation,
  getPresentationById,
} from '../../../../api/presentations-management';
import type { PresentationFormData } from '../types';
import type { UseListFetchConfig } from '../../../../hooks';
import type { BriefPresentatiionDto } from '../../../../services/presentations-management/models/BriefPresentatiionDto';
import type { UpdatePresentationRequest } from '../../../../services/presentations-management/models/UpdatePresentationRequest';
import type { PresentationDto } from '../../../../services/presentations-management/models/PresentationDto';

export const usePresentationsManagement = (config?: UseListFetchConfig) => {
  const { isOpened, onClose, onOpen } = useModal();
  const {
    isOpened: isOpenedEditModal,
    onClose: onCloseEditModal,
    onOpen: onOpenEditModal,
  } = useModal();
  const {
    isOpened: isOpenedDeleteModal,
    onClose: onCloseDeleteModal,
    onOpen: onOpenDeleteModal,
  } = useModal();
  const {
    isOpened: isOpenedViewModal,
    onClose: onCloseViewModal,
    onOpen: onOpenViewModal,
  } = useModal();

  const [selectedPresentation, setSelectedPresentation] =
    useState<BriefPresentatiionDto | null>(null);
  const [presentationDetails, setPresentationDetails] =
    useState<PresentationDto | null>(null);

  const {
    nodes: presentations,
    filters,
    setFilters,
    setSort,
    loading,
    refresh,
    setPage,
    pagination,
  } = useListFetch(
    async params => {
      return await getPresentations({
        search: params?.filters?.search,
        page: params?.pagination?.page || 0,
        size: params?.pagination?.perPage || 10,
        sort: params?.sort
          ? [
              `${params.sort.name || 'title'},${params.sort.direction || 'desc'}`,
            ]
          : undefined,
      });
    },
    {
      ...config,
      resultKey: 'content',
      withQueryParams: true,
    }
  );

  const [handleCreatePresentation, creatingPresentation] = useRequest(
    async (_signal, payload: PresentationFormData) => {
      return await createPresentation({
        title: payload.title,
        description: payload.description,
        file: payload.file!,
      });
    },
    {
      onSuccess: () => {
        notify.success('Presentation created successfully');
        refresh();
        onClose();
      },
      onError: () => {
        notify.error('Failed to create presentation');
      },
    }
  );

  const [handleUpdatePresentation, updatingPresentation] = useRequest(
    (_signal, payload: { id: string; data: UpdatePresentationRequest }) => {
      return updatePresentation({
        id: payload.id,
        data: payload.data,
      });
    },
    {
      onSuccess: () => {
        notify.success('Presentation updated successfully');
        refresh();
        onCloseEditModal();
        setSelectedPresentation(null);
      },
      onError: () => {
        notify.error('Failed to update presentation');
      },
    }
  );

  const [handleDeletePresentation, deletingPresentation] = useRequest(
    (_signal, presentationId: string) => {
      return deletePresentation(presentationId);
    },
    {
      onSuccess: () => {
        notify.success('Presentation deleted successfully');
        refresh();
        onCloseDeleteModal();
        setSelectedPresentation(null);
      },
      onError: () => {
        notify.error('Failed to delete presentation');
      },
    }
  );

  const [handleFetchPresentationDetails, loadingDetails] = useRequest(
    (_signal, presentationId: string) => {
      return getPresentationById(presentationId);
    },
    {
      onSuccess: data => {
        setPresentationDetails(data);
      },
      onError: () => {
        notify.error('Failed to fetch presentation details');
        onCloseViewModal();
      },
    }
  );

  const handleCreatePresentationSubmit = (data: PresentationFormData) => {
    console.log(data);

    if (data.file) {
      handleCreatePresentation(data);
    }
  };

  const handleEditPresentationSubmit = (data: PresentationFormData) => {
    if (selectedPresentation?.id) {
      handleUpdatePresentation({
        id: selectedPresentation.id,
        data: {
          title: data.title,
          description: data.description,
        },
      });
    }
  };

  const handleEditClick = (presentation: BriefPresentatiionDto) => {
    setSelectedPresentation(presentation);
    onOpenEditModal();
  };

  const handleDeleteClick = (presentation: BriefPresentatiionDto) => {
    setSelectedPresentation(presentation);
    onOpenDeleteModal();
  };

  const handleConfirmDelete = () => {
    if (selectedPresentation?.id) {
      handleDeletePresentation(selectedPresentation.id);
    }
  };

  const handleCloseEditModal = () => {
    onCloseEditModal();
    setSelectedPresentation(null);
  };

  const handleCloseDeleteModal = () => {
    onCloseDeleteModal();
    setSelectedPresentation(null);
  };

  const handleViewClick = (presentation: BriefPresentatiionDto) => {
    setSelectedPresentation(presentation);
    setPresentationDetails(null);
    onOpenViewModal();
    if (presentation.id) {
      handleFetchPresentationDetails(presentation.id);
    }
  };

  const handleCloseViewModal = () => {
    onCloseViewModal();
    setSelectedPresentation(null);
    setPresentationDetails(null);
  };

  return {
    presentations: presentations as BriefPresentatiionDto[],
    loading,
    filters,
    pagination,
    selectedPresentation,
    presentationDetails,
    isOpened,
    onOpen,
    onClose,
    isOpenedEditModal,
    onCloseEditModal: handleCloseEditModal,
    isOpenedDeleteModal,
    onCloseDeleteModal: handleCloseDeleteModal,
    isOpenedViewModal,
    onCloseViewModal: handleCloseViewModal,
    setFilters,
    setSort,
    setPage,
    handleCreatePresentationSubmit,
    handleEditPresentationSubmit,
    handleEditClick,
    handleDeleteClick,
    handleViewClick,
    handleConfirmDelete,
    creatingPresentation,
    updatingPresentation,
    deletingPresentation,
    loadingDetails,
  };
};
